import { z } from 'zod';
import { LanguageSchema, RoleSchema } from './enums.js';
import {
  EntitateSilvicaListSchema,
  NAME_MAX,
  NAME_MIN,
  PasswordSchema,
  UsernameSchema,
  UserPublicSchema,
} from './auth.js';

/**
 * Admin-side user creation. Reuses the same username/password policy as
 * self-registration so admin-created accounts can't be given a weak
 * password, and validates role/language against their enums.
 */
export const CreateAdminUserInputSchema = z
  .object({
    username: UsernameSchema,
    password: PasswordSchema,
    name: z.string().min(NAME_MIN).max(NAME_MAX),
    role: RoleSchema,
    preferredLanguage: LanguageSchema.optional(),
    managerId: z.string().uuid().nullable().optional(),
  })
  .strict();
export type CreateAdminUserInput = z.infer<typeof CreateAdminUserInputSchema>;

export const AdminUserSchema = UserPublicSchema.extend({
  email: z.string().email().nullable(),
  managerId: z.string().uuid().nullable(),
  lastLoginAt: z.string().nullable(),
  updatedAt: z.string(),
});
export type AdminUser = z.infer<typeof AdminUserSchema>;

export const UpdateMeInputSchema = z
  .object({
    fullName: z.string().min(2).max(100).optional(),
    email: z.string().email().nullable().optional(),
    preferredLanguage: LanguageSchema.optional(),
    emailNotifications: z.boolean().optional(),
    // Push/in-app notification opt-out (ADR-016). Self-editable; the only
    // notification field a user may toggle on themselves. When false,
    // `sendPushFanout` skips the user's devices but in-app rows still accrue.
    notificationsEnabled: z.boolean().optional(),
    // assignedEntitateSilvica / assignedEntitatiSilvice are intentionally absent — admin-only mutation.
    // Sending either the singular or plural key fails Zod .strict() (unrecognized_keys).
    defaultRaion: z.string().max(100).nullable().optional(),
    defaultPrimarie: z.string().max(100).nullable().optional(),
    defaultUcf: z.string().max(50).nullable().optional(),
    defaultOcolSilvic: z.string().max(100).nullable().optional(),
  })
  .strict()
  .refine((v) => Object.keys(v).length > 0, { message: 'At least one field required' });
export type UpdateMeInput = z.infer<typeof UpdateMeInputSchema>;

export const ChangePasswordInputSchema = z
  .object({
    currentPassword: z.string().min(1),
    newPassword: PasswordSchema,
  })
  .strict();
export type ChangePasswordInput = z.infer<typeof ChangePasswordInputSchema>;

export const UpdateAdminUserInputSchema = z
  .object({
    role: RoleSchema.optional(),
    isActive: z.boolean().optional(),
    fullName: z.string().min(2).max(100).optional(),
    cod: z
      .string()
      .max(32)
      .regex(/^[A-Za-z0-9_\-]+$/, { message: 'cod allows letters, digits, underscore, dash' })
      .nullable()
      .optional(),
    managerId: z.string().uuid().nullable().optional(),
    /**
     * Canonical multi-value field. Omit = no change.
     * Empty array = clear assignment. null is rejected (use [] instead).
     */
    assignedEntitatiSilvice: EntitateSilvicaListSchema.optional(),
  })
  .strict();
export type UpdateAdminUserInput = z.infer<typeof UpdateAdminUserInputSchema>;

export const AdminUsersQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(1000).default(50),
  sort: z.string().optional(),
  order: z.enum(['asc', 'desc']).optional(),
  q: z.string().optional(),
  role: RoleSchema.optional(),
  isActive: z.coerce.boolean().optional(),
});
export type AdminUsersQuery = z.infer<typeof AdminUsersQuerySchema>;
